import { useState, useRef, useCallback, useEffect } from 'react'
import { AudioResult } from './api/mutations/ft-voice-note/useFtVoiceNote'

export type { AudioResult }

interface UseVoiceRecorderReturn {
  isRecording: boolean
  startRecording: () => Promise<void>
  stopRecording: () => Promise<AudioResult>
  duration: number
}

export const useVoiceRecorder = (): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false)
  const [duration, setDuration] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const resolveStopRef = useRef<((result: AudioResult) => void) | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
    mediaRecorderRef.current = mediaRecorder
    chunksRef.current = []

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
      const uri = URL.createObjectURL(blob)
      stream.getTracks().forEach((track) => track.stop())
      if (resolveStopRef.current) {
        resolveStopRef.current({ uri, type: 'audio/webm', name: 'voice-note.webm', blob })
        resolveStopRef.current = null
      }
    }

    mediaRecorder.start()
    setIsRecording(true)
    setDuration(0)
    timerRef.current = setInterval(() => {
      setDuration((d) => d + 1)
    }, 1000)
  }, [])

  const stopRecording = useCallback((): Promise<AudioResult> => {
    return new Promise((resolve) => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setIsRecording(false)
      resolveStopRef.current = resolve
      mediaRecorderRef.current?.stop()
    })
  }, [])

  return { isRecording, startRecording, stopRecording, duration }
}
